import React, { useState, useEffect, useMemo } from "react";
import type { FC, PropsWithChildren } from "react";
import { UndoOutlined } from "@ant-design/icons";

import { Modal, Button } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";

import {
  useGetBillingMinutes,
  useUpdateBillingMinutes,
  useAddNote,
  useFetchNotes,
} from "../../hooks/patients";
import {
  IPatientRes,
  IBillingMinute,
  IBillingRecordUpdate,
} from "../../hooks/patients/index.type";
import ManualTimerSkeleton from "../../styles/ManualTimerSkeleton";
import classes from "../../styles/ManualTimer.module.css";

export type ManualTimerModalState = PropsWithChildren<{
  status: boolean;
}>;

type ManualTimerModalProps = PropsWithChildren<{
  ManualTimerModal: ManualTimerModalState;
  onCloseModal: () => void;
  patientId: string;
  clinicId: string;
  billingCycleId: string;
  onSuccess?: () => void;
  patientData?: IPatientRes;
  patientName?: string;
  setPatientData?: React.Dispatch<
    React.SetStateAction<IPatientRes | undefined>
  >;
  getPatientData?: () => Promise<void>;
}>;

// function validateSections(data: IBillingRecordUpdate[]) {
//   const sectionTotals: any = {};
//   let totalMinutes = 0;

//   for (const item of data) {
//     if (item.action === "remove") continue;

//     sectionTotals[item.section] =
//       (sectionTotals[item.section] || 0) + item.billingInMinutes;

//     totalMinutes += item.billingInMinutes;
//   }

//   const FIRST = sectionTotals.FIRST || 0;
//   const SECOND = sectionTotals.SECOND || 0;
//   const THIRD = sectionTotals.THIRD || 0;
//   const FOURTH = sectionTotals.FOURTH || 0;

//   // FIRST must exist
//   if (!FIRST) {
//     return {
//       valid: false,
//       message: totalMinutes === 0 ? "" : "FIRST section missing",
//       sectionTotals,
//     };
//   }

//   // If THIRD exists but SECOND missing → invalid
//   if (THIRD && !SECOND) {
//     return {
//       valid: false,
//       message: "SECOND section must exist before THIRD",
//       sectionTotals,
//     };
//   }

//   // If FOURTH exists but THIRD missing → invalid
//   if (FOURTH && !THIRD) {
//     return {
//       valid: false,
//       message: "THIRD section must exist before FOURTH",
//       sectionTotals,
//     };
//   }

//   // Minutes validation
//   if (FIRST !== 20) {
//     return {
//       valid: false,
//       message: `FIRST section minutes must be 20 but got ${sectionTotals.FIRST}`,
//       sectionTotals,
//     };
//   }

//   if (SECOND && SECOND !== 20) {
//     return {
//       valid: false,
//       message: `SECOND section minutes must be 20 got ${sectionTotals.SECOND}`,
//       sectionTotals,
//     };
//   }

//   if (THIRD && THIRD !== 20) {
//     return {
//       valid: false,
//       message: `THIRD section minutes must be 20 got ${sectionTotals.THIRD}`,
//       sectionTotals,
//     };
//   }

//   return {
//     valid: true,
//     sectionTotals,
//     totalMinutes,
//     message: "",
//   };
// }

const ManualTimerModals: FC<ManualTimerModalProps> = (props) => {
  const [billingRows, setBillingRows] = useState<IBillingMinute[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [deletedRowIds, setDeletedRowIds] = useState<Set<string>>(new Set());
  const [tempRowData, setTempRowData] = useState<Partial<IBillingMinute>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newMinutes, setNewMinutes] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  const { billingMinutesData, getBillingMinutesData, billingMinutesLoading } =
    useGetBillingMinutes({
      billingCycleId: props.billingCycleId,
    });
  const { updateBillingMinutesData, updateBillingMinutesLoading } =
    useUpdateBillingMinutes({
      billingCycleId: props.billingCycleId,
    });
  const { addNote } = useAddNote({
    patientId: props.patientId,
  });
  const { getNotes } = useFetchNotes({
    patientId: props.patientId,
  });

  useEffect(() => {
    if (props.ManualTimerModal.status && props.billingCycleId) {
      getBillingMinutesData();
    }
  }, [props.ManualTimerModal.status, props.billingCycleId]);

  useEffect(() => {
    if (billingMinutesData?.status === "success") {
      setBillingRows(billingMinutesData.data);
    }
  }, [billingMinutesData]);

  const handleEdit = (row: IBillingMinute) => {
    setEditingRowId(row._id);
    setTempRowData({ ...row });
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setTempRowData({});
  };

  const handleDoneEdit = () => {
    if (editingRowId === null) return;
    setBillingRows((prev) =>
      prev.map((row) =>
        row._id === editingRowId
          ? ({ ...row, ...tempRowData } as IBillingMinute)
          : row,
      ),
    );
    setEditingRowId(null);
    setTempRowData({});
  };

  const handleDelete = (id: string) => {
    setDeletedRowIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const handleUndo = (id: string) => {
    setDeletedRowIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const activeRows = useMemo(() => {
    return billingRows.filter((row) => !deletedRowIds.has(row._id));
  }, [billingRows, deletedRowIds]);

  const totalMinutes = useMemo(() => {
    return activeRows.reduce(
      (acc, row) => acc + (Number(row.billingInMinutes) || 0),
      0,
    );
  }, [activeRows]);

  // const validationResult = useMemo(() => {
  //   const records: IBillingRecordUpdate[] = [
  //     ...billingRows
  //       .filter((row) => !deletedRowIds.has(row._id))
  //       .map((row) => ({
  //         _id: row._id,
  //         billingInMinutes: row.billingInMinutes,
  //         section: row.section as any,
  //         action: "update" as const,
  //       })),
  //     ...Array.from(deletedRowIds).map((id) => {
  //       const row = billingRows.find((r) => r._id === id);
  //       return {
  //         _id: id,
  //         billingInMinutes: row?.billingInMinutes ?? 0,
  //         section: row?.section ?? ("FIRST" as any),
  //         action: "remove" as const,
  //       };
  //     }),
  //   ];
  //   // return validateSections(records);
  // }, [billingRows, deletedRowIds]);

  const handleSaveNewRow = () => {
    if (newMinutes <= 0) {
      return;
    }

    const newRow: IBillingMinute = {
      _id: "",
      billingInMinutes: newMinutes,
      createdTimestamp: Date.now(),
      section: " ",
      billingCode: "",
    };

    setBillingRows((prev) => [newRow, ...prev]);
    setIsAdding(false);
    setNewMinutes(0);
  };

  const handleSave = async () => {
    if (isSaving || updateBillingMinutesLoading) return;
    setIsSaving(true);
    try {
      const billingRecords: IBillingRecordUpdate[] = [
        ...billingRows
          .filter((row) => !deletedRowIds.has(row._id))
          .map((row) => ({
            _id: row._id,
            billingInMinutes: row.billingInMinutes,
            section: row.section,
            action: row._id ? ("update" as const) : ("add" as const),
          })),
        ...Array.from(deletedRowIds)
          .filter((id) => !!id)
          .map((id) => {
            const row = billingRows.find((r) => r._id === id);
            return {
              _id: id,
              billingInMinutes: row?.billingInMinutes ?? 0,
              section: row?.section ?? "FIRST",
              action: "remove" as const,
            };
          }),
      ];

      if (isAdding && newMinutes > 0) {
        billingRecords.push({
          billingInMinutes: newMinutes,
          section: " ",
          action: "add" as const,
        });
      }

      const hasAddAction = billingRecords.some(
        (r: IBillingRecordUpdate) => r.action === "add",
      );
      const res = await updateBillingMinutesData({ billingRecords });

      if (res?.status === "success") {
        if (props.setPatientData && res?.data) {
          const updatedPatient = res.data;
          props.setPatientData((prev) => {
            if (!prev) return undefined;
            const newPatientData = prev?.data?.data?.map((p) => {
              if (p._id === updatedPatient._id) {
                return { ...p, timeSpent: updatedPatient.timeSpent };
              }
              return p;
            });
            return {
              ...prev,
              data: {
                ...prev.data,
                data: newPatientData,
              },
            };
          });
        }

        props.onCloseModal();
        const billingSessionId = res.billingSessionId;

        if (hasAddAction && billingSessionId) {
          addNote({
            noteCategory: "timer-summary",
            note: "Timer Summary - Software-verified timestamp confirms recorded activity.",
            richText:
              "<p>Timer Summary - Software-verified timestamp confirms recorded activity.</p>",
            type: "general-note",
            billingSessionId: billingSessionId,
          }).then((response) => {
            if (response?.status === "success") {
              getNotes();
            }
          });
        }

        if (props.onSuccess) {
          props.onSuccess();
        }

        setDeletedRowIds(new Set());
        setEditingRowId(null);
        setIsAdding(false);
        setNewMinutes(0);
      }
    } finally {
      setIsSaving(false);
    }
  };
  const TIMEZONE = "America/New_York";

  const formatDateByTimezone = (timestamp: number) => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(timestamp));
  };

  const isDisabled = updateBillingMinutesLoading || isSaving;

  return (
    <Modal
      show={props.ManualTimerModal.status}
      modalClassName={classes.manual_timer_modal}
    >
      <div className={classes.manual_timer_header_wrapper}>
        <ModalHeading
          heading="Manual Entry (Minutes)"
          customClass={classes.manual_timer_heading}
        />
      </div>
      <div className={classes.patient_info_row}>
        {props.patientName && (
          <div className={classes.patient_name_display}>
            Patient Name: <span>{props.patientName}</span>
          </div>
        )}
        <button
          className={classes.add_minutes_top_btn}
          onClick={() => setIsAdding(true)}
          disabled={isAdding || activeRows.some((row) => row._id === "")}
        >
          Add Minutes
        </button>
      </div>

      <div className={classes.billing_table_container}>
        <table className={classes.billing_table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Billing Min</th>
              {/* <th>Section</th>
              <th>Billing Code</th> */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {billingMinutesLoading ? (
              <ManualTimerSkeleton rows={3} />
            ) : (
              <>
                {isAdding && (
                  <tr className={classes.billing_row_adding}>
                    <td>
                      <span className={classes.billing_code_text}>
                        {formatDateByTimezone(Date.now())}
                      </span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className={classes.billing_input}
                        value={newMinutes === 0 ? "" : newMinutes}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          setNewMinutes(val === "" ? 0 : Number(val));
                        }}
                        autoFocus
                        placeholder="Enter Minutes"
                      />
                    </td>
                    <td>
                      <div className={classes.action_buttons}>
                        <button
                          className={classes.done_btn}
                          onClick={handleSaveNewRow}
                        >
                          Done
                        </button>
                        <button
                          className={classes.cancel_row_btn}
                          onClick={() => {
                            setIsAdding(false);
                            setNewMinutes(0);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {(billingRows?.length ?? 0) > 0
                  ? billingRows?.map((row) => {
                      const isEditing = editingRowId === row._id;
                      const isDeleted = deletedRowIds.has(row._id);

                      return (
                        <tr
                          key={row._id}
                          className={`${isEditing ? classes.billing_row_editing : ""} ${isDeleted ? classes.billing_row_deleted : ""}`}
                        >
                          <td>
                            <span className={classes.billing_code_text}>
                              {formatDateByTimezone(row?.createdTimestamp) ||
                                "-"}
                            </span>
                          </td>
                          <td>
                            {isEditing ? (
                              <input
                                type="text"
                                className={classes.billing_input}
                                value={
                                  tempRowData.billingInMinutes === 0
                                    ? ""
                                    : tempRowData.billingInMinutes
                                }
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "");
                                  setTempRowData({
                                    ...tempRowData,
                                    billingInMinutes:
                                      val === "" ? 0 : Number(val),
                                  });
                                }}
                              />
                            ) : (
                              <span className={classes.billing_min_text}>
                                {row?.billingInMinutes}
                                <span className={classes.billing_min_unit}>
                                  min
                                </span>
                              </span>
                            )}
                          </td>
                          <td>
                            <div className={classes.action_buttons}>
                              {isEditing ? (
                                <>
                                  <button
                                    className={classes.done_btn}
                                    onClick={handleDoneEdit}
                                  >
                                    Done
                                  </button>
                                  <button
                                    className={classes.cancel_row_btn}
                                    onClick={handleCancelEdit}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : isDeleted ? (
                                <button
                                  className={classes.undo_btn}
                                  onClick={() => handleUndo(row._id)}
                                >
                                  <UndoOutlined /> Undo
                                </button>
                              ) : (
                                <>
                                  <button
                                    className={classes.edit_btn}
                                    onClick={() => handleEdit(row)}
                                  >
                                    Change Minutes
                                  </button>
                                  <button
                                    className={classes.delete_btn}
                                    onClick={() => handleDelete(row._id)}
                                  >
                                    Remove
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  : !isAdding && (
                      <tr>
                        <td colSpan={4} className={classes.no_data_text}>
                          No Data Available
                        </td>
                      </tr>
                    )}
              </>
            )}
          </tbody>
          {(billingRows?.length ?? 0) > 0 && (
            <tfoot>
              <tr className={classes.total_row}>
                <td>
                  <span className={classes.total_label}>Total Minutes</span>
                </td>
                <td>
                  <span className={classes.total_min_text}>
                    {totalMinutes}
                    <span className={classes.billing_min_unit}>min</span>
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {/* {!validationResult.valid && (
        <div style={{ padding: "0 1.5rem", marginTop: "1rem" }}>
          <p className={classes.errorText} style={{ margin: "0.2rem 0" }}>
            {validationResult.message}
          </p>
        </div>
      )} */}

      <div className={classes.manual_timer_button_container}>
        <Button
          label="Cancel"
          span={false}
          customClass={`${classes.manual_timer_button} ${classes.manual_timer_cancel_button}`}
          onClick={props.onCloseModal}
        />
        <Button
          label={isDisabled ? "Saving..." : "Save"}
          span={false}
          disabled={isDisabled}
          customClass={`${classes.manual_timer_button} ${
            isDisabled ? classes.save_button_disabled : classes.save_button_blue
          }`}
          onClick={handleSave}
        />
      </div>
    </Modal>
  );
};

export default ManualTimerModals;
